import { useMutation, useQueryClient } from '@tanstack/react-query';

import solomonService from 'services/solomonService';

import type {
  SolomonExecuteActionRequest,
  SolomonExecuteActionResponse,
  SolomonPageContext,
} from '../../types/solomon';

interface UseSolomonExecuteActionProps {
  artistId: string;
  pageContext: SolomonPageContext;
  onSuccess?: (data: SolomonExecuteActionResponse, variables: SolomonExecuteActionRequest) => void;
  onError?: (error: Error, variables: SolomonExecuteActionRequest) => void;
}

export function useSolomonExecuteAction({ artistId, pageContext, onSuccess, onError }: UseSolomonExecuteActionProps) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (request: SolomonExecuteActionRequest) => {
      return solomonService.executeAction({
        artistId,
        ...request,
      });
    },
    onSuccess: (data, variables) => {
      // Optionally invalidate queries if needed
      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: ['solomon-recommendations', artistId, pageContext],
        });
      }
      onSuccess?.(data, variables);
    },
    onError: (error: Error, variables) => {
      onError?.(error, variables);
    },
  });

  return {
    executeAction: mutation.mutateAsync,
    isExecuting: mutation.isPending,
    error: mutation.error,
    ...mutation,
  };
}

export default useSolomonExecuteAction;
